/**
 * analytics-shared/hooks/useFolders.js
 *
 * Folder list + mutation hooks for the reports and dashboards modules.
 *
 * Why we use setQueryData instead of invalidateQueries on success:
 *   The queryShim's invalidateQueries flips the cache entry's timestamp
 *   to 0 and notifies subscribers, but useQuery's effect only re-runs
 *   when [key, enabled, staleTime] change — none of which do — so the
 *   queryFn never re-fires and the UI keeps showing the old list.
 *   setQueryData writes the new state directly into the cache AND
 *   notifies subscribers, which gives us the "instant sidebar refresh"
 *   behaviour the user expects without round-tripping the queryFn.
 *
 *   This is also the canonical optimistic-update pattern in React Query
 *   proper, so when we swap the shim for @tanstack/react-query this hook
 *   keeps working unchanged.
 */
import {
  useQuery, useMutation, getQueryData, setQueryData, invalidateQueries,
} from './queryShim';
import { foldersApi } from '../api/foldersApi';

const REPORT_KEY = ['folders', 'report'];
const DASHBOARD_KEY = ['folders', 'dashboard'];

// Reports folders are object-shaped: { id, name, color }.
export function useReportFolders() {
  const q = useQuery({
    queryKey: REPORT_KEY,
    queryFn: () => foldersApi.listReportFolders(),
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: (name) => foldersApi.createReportFolder(name),
    onSuccess: (folder) => {
      const current = getQueryData(REPORT_KEY) || [];
      // Guard against accidental duplicates (e.g. the API echoes a folder
      // that we optimistically inserted earlier).
      if (folder && !current.some((f) => f.id === folder.id)) {
        setQueryData(REPORT_KEY, [...current, folder]);
      }
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }) => foldersApi.renameReportFolder(id, name),
    onSuccess: (updated, vars) => {
      const current = getQueryData(REPORT_KEY) || [];
      const target = updated || (vars ? { id: vars.id, name: vars.name } : null);
      if (!target) return;
      setQueryData(
        REPORT_KEY,
        current.map((f) => (f.id === target.id ? { ...f, ...target } : f)),
      );
      // Cross-cache: report cards carry the folder name in a badge;
      // without this invalidation a renamed folder's reports keep
      // showing the old name until reload.
      invalidateQueries(['reports']);
    },
  });

  const remove = useMutation({
    mutationFn: (id) => foldersApi.deleteReportFolder(id),
    onSuccess: (_res, id) => {
      const current = getQueryData(REPORT_KEY) || [];
      setQueryData(REPORT_KEY, current.filter((f) => f.id !== id));
      // Cross-cache: backend cascades reports in the deleted folder
      // back to "Unfiled" — the list cache has to refetch so the
      // re-parented cards show their new folder badge.
      invalidateQueries(['reports']);
    },
  });

  return { ...q, folders: q.data || [], create, rename, remove };
}

// Dashboard folders are currently a flat list of strings. Mutators take care
// to keep that shape until both modules unify on the object shape.
export function useDashboardFolders() {
  const q = useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: () => foldersApi.listDashboardFolders(),
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: (name) => foldersApi.createDashboardFolder(name),
    onSuccess: (name) => {
      const current = getQueryData(DASHBOARD_KEY) || [];
      if (name && !current.includes(name)) {
        setQueryData(DASHBOARD_KEY, [...current, name]);
      }
    },
  });

  const rename = useMutation({
    mutationFn: ({ oldName, newName }) => foldersApi.renameDashboardFolder(oldName, newName),
    onSuccess: (newName, vars) => {
      const current = getQueryData(DASHBOARD_KEY) || [];
      const oldName = vars?.oldName;
      if (!oldName || !newName || oldName === newName) return;
      setQueryData(
        DASHBOARD_KEY,
        current.map((n) => (n === oldName ? newName : n)),
      );
      invalidateQueries(['dashboards']);
    },
  });

  const remove = useMutation({
    mutationFn: (name) => foldersApi.deleteDashboardFolder(name),
    onSuccess: (_res, name) => {
      const current = getQueryData(DASHBOARD_KEY) || [];
      setQueryData(DASHBOARD_KEY, current.filter((n) => n !== name));
      invalidateQueries(['dashboards']);
    },
  });

  return { ...q, folders: q.data || [], create, rename, remove };
}
